import * as React from "react";
import {
  Link,
  LoaderFunctionArgs,
  redirect,
  useLoaderData,
  useSearchParams,
} from "react-router-dom";
import { LoaderData } from "./MyAccount";
import { AccountHolder, useAccount } from "../../Account";
import { UserView, convertPenniesToCurrency } from "../../AccountMenu";

import {
  LocationConfiguration,
  LocationConfigurationHolder,
} from "../../LocationConfiguration";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import { useLanguage } from "../../Language";
import axios from "axios";
import { BorrowerNotificationType } from "@tlc/ls2pac/dto/BorrowerNotificationType";
import { GoogleSignInButton } from "../../GoogleSignInButton";
import {
  Alert,
  Avatar,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
} from "@mui/material";
import { SuccessMessageControl } from "@tlc/ls2pac/app/controls/successMessageControl";
import { GlobalConfig, GlobalConfigHolder } from "../../GlobalConfig";
import { LinkedGoogleAccount } from "../../LinkedGoogleAccount";
import { BorrowerChild } from "@tlc/ls2pac/dto/BorrowerChild";
import { Patron } from "@tlc/ls2pac/dto/Patron";
import { useDialog } from "@tlc/ui/dialog/useDialog";

export enum BorrowerBlockStatus {
  NotBlocked = "NotBlocked",
  Blocked = "Blocked",
  InCollection = "InCollection",
}

export interface BorrowerNotificationSettings {
  expiredCardNotificationTypes: Array<BorrowerNotificationType>;
  friendlyWarningNotificationTypes: Array<BorrowerNotificationType>;
  overdueNotificationTypes: Array<BorrowerNotificationType>;
  finalOverdueNotificationTypes: Array<BorrowerNotificationType>;
  arrivedHoldNotificationTypes: Array<BorrowerNotificationType>;
  cancelledHoldNotificationTypes: Array<BorrowerNotificationType>;
  automaticRenewalNotificationTypes: Array<BorrowerNotificationType>;
}

export interface BorrowerNotificationPermissions {
  canBorChangeFinalOverdue: boolean;
  canBorChangeArrivedHold: boolean;
  canBorChangeOverdue: boolean;
  canBorChangeCanceledHld: boolean;
  canBorChangeFriendlyWarn: boolean;
  canBorChangeAutoRenewal: boolean;
  canBorChangeAccntExpire: boolean;
}

export interface BorrowerAddress {
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  state: string;
  zipCode: string;
  phone1: string;
  phone2: string | null;
}

export interface BorrowerSummary {
  agencyNumber: number;
  responsibleAgencyNumber: number;
  salutation: string | null;
  firstName: string;
  middleName: string;
  lastName: string;
  nameSuffix: string | null;
  barcode: string;
  pin: string;
  blockStatus: string;
  emailAddress: string | null;
  emailAddress2: string | null;
  emailAddress3: string | null;
  expirationDate: unknown; // This ZonedDateTime serializes in an odd way, unless we're using it, it can remain unknown
  issuingBranch: number;
  holdsTotalCount: number;
  holdsArrivedCount: number;
  itemsTotalCount: number;
  itemsOverdueCount: number;
  fees: number;
  ableToAddDependentsFromPac: boolean;
  borrowerAddresses: BorrowerAddress[];
  notificationSettings: BorrowerNotificationSettings;
  notificationPermissions: BorrowerNotificationPermissions;
  smsPhone: string | null;
  smsGatewayId: string | null;
  children: BorrowerChild[];
  holdsPendingCount: number;
  cardExpired: boolean;
}

export function isSignInWithGoogleEnabled(
  user: UserView,
  locationConfiguration: LocationConfiguration,
  { googleOauthEnabled, adfsSettings }: GlobalConfig,
): boolean {
  // ADFS SSO workflow does not support Sign in with Google.
  return (
    user?.local &&
    locationConfiguration?.pacSettings?.googleSSOEnabled &&
    googleOauthEnabled &&
    adfsSettings.enabled === false
  );
}

export function isDependentsGridEnabled(
  patron: Patron,
  locationConfiguration: LocationConfiguration,
): boolean {
  // patron is already a dependent of a responsible party
  if (patron?.responsibleAgencyNumber != null) {
    return false;
  }

  return locationConfiguration?.pacSettings?.displaySettings
    ?.showDependentBorrowers;
}

export function isAddDependentLinkEnabled(
  patron: Patron,
  locationConfiguration: LocationConfiguration,
  { obrEnabled }: GlobalConfig,
): boolean {
  // patron is already a dependent of a responsible party
  if (patron?.responsibleAgencyNumber != null) {
    return false;
  }

  // TODO should this be possible in an AD or SSO setup?
  // capable of adding dependent to responsible party
  return (
    obrEnabled &&
    locationConfiguration?.pacSettings?.obrEnabled &&
    patron?.ableToAddDependentsFromPac
  );
}

export function isLinkedAccountsEnabled(
  user: UserView,
  patron: Patron,
  locationConfiguration: LocationConfiguration,
  globalConfig: GlobalConfig,
): boolean {
  if (!patron) {
    return false;
  }
  return (
    isSignInWithGoogleEnabled(user, locationConfiguration, globalConfig) ||
    isDependentsGridEnabled(patron, locationConfiguration) ||
    isAddDependentLinkEnabled(patron, locationConfiguration, globalConfig)
  );
}

export async function getDependentSummaries() {
  const { data } = await axios.get<BorrowerSummary[]>(
    "/account/summary/dependents",
  );
  return data;
}

function getFullName(borrowerSummary: BorrowerSummary): string {
  const {
    salutation = "",
    firstName = "",
    middleName = "",
    lastName,
    nameSuffix = "",
  } = borrowerSummary;

  return `${salutation ? salutation + " " : ""}${firstName}${
    middleName ? " " + middleName : ""
  } ${lastName}${nameSuffix ? " " + nameSuffix : ""}`;
}

// TODO add to language file translations?
function getStatusDisplay(borrowerSummary: BorrowerSummary): string {
  if (borrowerSummary.cardExpired) {
    return "Expired";
  }

  switch (borrowerSummary.blockStatus) {
    case BorrowerBlockStatus.NotBlocked:
      return "Active";
    case BorrowerBlockStatus.Blocked:
      return "Restricted";
    case BorrowerBlockStatus.InCollection:
      return "Restricted - CA";
    default:
      return "Unknown"; // Handle any unexpected block statuses
  }
}

async function unlinkGoogleAccount(patronId: string) {
  try {
    const { data } = await axios.post<{ success: boolean }>(
      "/account/update/unlinkGoogleAccount",
      {
        patronId,
      },
    );
    return data;
  } catch (e) {
    console.error(e);
    return { success: false };
  }
}

export async function linkedAccountsLoader({ request }: LoaderFunctionArgs) {
  console.log("linkedAccountsLoader executing");

  const { user, patron } = await AccountHolder.fetchAccountData();
  const globalConfig = await GlobalConfigHolder.fetchGlobalConfig();
  //   const {} = Global

  const locationCondfiguration =
    await LocationConfigurationHolder.fetchLocationConfiguration();
  console.log(
    "linkedAccountLoader locationconfiguration",
    locationCondfiguration,
  );

  const showLinkGoogleAccount = isSignInWithGoogleEnabled(
    user,
    locationCondfiguration,
    globalConfig,
  );
  console.log("showLinkGoogleAccount", showLinkGoogleAccount);
  const showDependentsGrid = isDependentsGridEnabled(
    patron,
    locationCondfiguration,
  );
  console.log("showDependentsGrid", showDependentsGrid);
  const showAddDependentLink = isAddDependentLinkEnabled(
    patron,
    locationCondfiguration,
    globalConfig,
  );
  console.log("showAddDependentLink", showAddDependentLink);

  if (
    showLinkGoogleAccount === false &&
    showDependentsGrid === false &&
    showAddDependentLink === false
  ) {
    console.log("Linked Accounts inaccessible, redirecting");
    return redirect("/myaccount/summary");
  }

  const dependentSummaries = showDependentsGrid
    ? await getDependentSummaries()
    : [];

  return {
    dependentSummaries,
    showLinkGoogleAccount,
    showDependentsGrid,
    showAddDependentLink,
  };
}

export const LinkedAccountsPage: React.FC = () => {
  const { patron } = useAccount();
  const { translate, dictionary } = useLanguage();
  const [searchParams] = useSearchParams();
  const showAccountAlreadyLinkedMsg =
    searchParams.get("showAccountAlreadyLinkedMsg") === "true";
  const showAccountLinkedMsg =
    searchParams.get("showAccountLinkedMsg") === "true";
  const {
    showLinkGoogleAccount,
    showDependentsGrid,
    showAddDependentLink,
    dependentSummaries,
  } = useLoaderData() as LoaderData<typeof linkedAccountsLoader>;

  const {
    open: confirmDeleteOpen,
    openDialog: openConfirmDeleteDialog,
    closeDialog: closeConfirmDeleteDialog,
  } = useDialog();
  const [deleting, setDeleting] = React.useState<boolean>(false);
  const [unlinkError, setUnlinkError] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (showAccountLinkedMsg && dictionary) {
      SuccessMessageControl.show(
        translate("successLinkingGoogleAccountMessage"),
      );
    }
  }, [showAccountLinkedMsg, dictionary]);

  const handleDelete = () => {
    setDeleting(false);
    openConfirmDeleteDialog();
  };

  const handleSubmitDelete = async () => {
    setDeleting(true);
    setUnlinkError(false);
    const { success } = await unlinkGoogleAccount(patron.patronId);

    if (success === false) {
      setUnlinkError(true);
      setDeleting(false);
    } else {
      await AccountHolder.fetchPatron(true);
      closeConfirmDeleteDialog();
      SuccessMessageControl.show(
        translate("successUnlinkingGoogleAccountMessage"),
      );
    }
  };

  const handleClose = () => {
    if (deleting) {
      return;
    }
    closeConfirmDeleteDialog();
  };

  return (
    <>
      {(showDependentsGrid || showAddDependentLink) && (
        <Paper
          sx={{
            mb: 2,
            display: "flex",
            flexDirection: "column",
            // maxWidth: "100%",
            maxWidth: "calc(100vw - 48px)", // Fragile but 100% doesn't work, 24px padding on parent
            position: "relative",
            boxSizing: "border-box",
            overflow: "hidden",
          }}
        >
          <Toolbar sx={{ flexWrap: "wrap", gap: 1 }}>
            <Typography
              sx={{ flex: "1 1", textWrap: "nowrap" }}
              variant="h6"
              component="div"
            >
              {translate("linkedBorrowerAccounts")}
            </Typography>
            {showAddDependentLink && (
              <Link to={"/create-account"} style={{ flexShrink: 0 }}>
                {translate("obrDialogAddDependantTitle")}
              </Link>
            )}
          </Toolbar>
          {dependentSummaries.length > 0 && (
            <Typography
              sx={{ mt: 1, mb: 1, pl: 2, pr: 2 }}
              variant={"subtitle1"}
            >
              {translate("linkedBorrowersYouAreResponsible")}
            </Typography>
          )}
          {dependentSummaries.length === 0 && (
            <Typography
              sx={{ mt: 1, mb: 1, pl: 2, pr: 2 }}
              variant={"subtitle1"}
            >
              {translate("linkedBorrowerAccountsNoneFound")}
            </Typography>
          )}
          {showDependentsGrid && dependentSummaries.length > 0 && (
            <TableContainer>
              <Table
                size={"medium"}
                sx={{
                  maxWidth: "100%",
                  width: "100%",
                  overflow: "auto",
                  flexGrow: 1,
                  flexShrink: 1,
                  flexBasis: "100%",
                  boxSizing: "border-box",
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell align={"left"} padding={"normal"}>
                      {translate("linkedBorrowerAccounts_Name")}
                    </TableCell>
                    <TableCell align={"left"} padding={"normal"}>
                      {translate("accountStatus")}
                    </TableCell>
                    <TableCell align={"right"} padding={"normal"}>
                      {translate("linkedBorrowerAccounts_ItemsOut")}
                    </TableCell>
                    <TableCell align={"right"} padding={"normal"}>
                      {translate("linkedBorrowerAccounts_ItemsOverdue")}
                    </TableCell>
                    <TableCell align={"right"} padding={"normal"}>
                      {translate("totalRequests")}
                    </TableCell>
                    <TableCell align={"right"} padding={"normal"}>
                      {translate("linkedBorrowerAccounts_ArrivedHolds")}
                    </TableCell>
                    <TableCell align={"right"} padding={"normal"}>
                      {translate("linkedBorrowerAccounts_Fines")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dependentSummaries.map((row, index) => {
                    return (
                      <TableRow key={row.barcode}>
                        <TableCell align={"left"} sx={{ textWrap: "nowrap" }}>
                          {getFullName(row)}
                        </TableCell>
                        <TableCell align="left">
                          {getStatusDisplay(row)}
                        </TableCell>
                        <TableCell align="right">
                          {row.itemsTotalCount}
                        </TableCell>
                        <TableCell align="right">
                          {row.itemsOverdueCount}
                        </TableCell>
                        <TableCell align="right">
                          {row.holdsTotalCount}
                        </TableCell>
                        <TableCell align="right">
                          {row.holdsArrivedCount}
                        </TableCell>
                        <TableCell align="right">
                          {convertPenniesToCurrency(row.fees)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {showLinkGoogleAccount && (
        <>
          <Toolbar disableGutters>
            <Typography sx={{ flex: "1 1 100%" }} variant="h6" component="div">
              {translate("linkedSignInAccounts")}
            </Typography>
          </Toolbar>
          <Typography sx={{ mb: 1 }} variant={"subtitle1"}>
            {translate("linkedAccountsSubtitle")}
          </Typography>
          {showAccountAlreadyLinkedMsg && (
            <Alert sx={{ mb: 1 }} severity="error">
              {translate("googleAccountAlreadyLinked")}
            </Alert>
          )}
          {!patron?.googleId && (
            <GoogleSignInButton
              onClick={() => (location.href = "/oauth2/linkAccount")}
              sx={{ mb: 4 }}
            ></GoogleSignInButton>
          )}
          {patron?.googleId && (
            <LinkedGoogleAccount
              email={patron.googleEmail}
              onClickUnlink={handleDelete}
            />
          )}
        </>
      )}
      <Dialog open={confirmDeleteOpen} onClose={handleClose}>
        <DialogContent>
          <DialogContentText>
            {translate("areYouSureUnlinkAccount")}
          </DialogContentText>
          {unlinkError && (
            <Alert severity="error">
              {translate("errorUnlinkingGoogleAccountMessage")}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={deleting} autoFocus onClick={handleClose}>
            {translate("cancel")}
          </Button>
          <Button
            disabled={deleting}
            variant="outlined"
            startIcon={
              deleting ? (
                <CircularProgress color="inherit" size={20} />
              ) : (
                <LinkOffIcon />
              )
            }
            onClick={handleSubmitDelete}
          >
            {translate("unlink")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
